import { Component, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  CeiAcaoLinhaTabela,
  CeiButtonComponent,
  CeiScrollableActionTableComponent,
  CeiScrollableActionTablePageFetcher,
  CeiSnackBarService,
  CeiTableColumn,
  CeiTablePage,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { notifyAdminListError } from '../../shared/admin-list.utils';
import {
  GerenciadorLarguraColunaId,
  atualizarColunasIdAoCarregarPagina,
} from '../../shared/admin-list-columns';
import { ADMIN_CARREGAMENTO_TABELA } from '../../shared/admin-loading.config';
import { PessoaListRow, PessoaUsuarioApiService } from '../../services/pessoa-usuario-api.service';

/** Listagem de pessoas/usuários com paginação por scroll e ações declarativas CEI. */
@Component({
  selector: 'cei-admin-list',
  standalone: true,
  imports: [CommonModule, RouterModule, CeiButtonComponent, CeiScrollableActionTableComponent],
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class AdminListComponent {
  private readonly colunasBase: CeiTableColumn<PessoaListRow>[] = [
    { key: 'id', label: 'ID', property: 'id', sortable: true },
    { key: 'nome', label: 'Nome', property: 'nome', sortable: true },
    { key: 'cpf', label: 'CPF', property: 'cpf', sortable: true },
    { key: 'login', label: 'Login', property: 'login', sortable: true },
    { key: 'email', label: 'E-mail', property: 'email', sortable: true },
  ];

  private readonly larguraColunaId = new GerenciadorLarguraColunaId();

  protected columns = this.larguraColunaId.aplicar(this.colunasBase);

  protected readonly carregamentoTabela = ADMIN_CARREGAMENTO_TABELA;

  protected readonly autoLoad = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly acoesLinha: CeiAcaoLinhaTabela<PessoaListRow>[] = [
    {
      id: 'editar',
      rotulo: 'Editar',
      icone: 'editar',
      rota: (linha) =>
        linha.pessoaId != null
          ? ['/admin/pessoas/atualizar', linha.pessoaId]
          : ['/admin/pessoas/atualizar'],
      queryParams: (linha) =>
        linha.pessoaId != null
          ? null
          : { login: linha.login !== '—' ? linha.login : String(linha.usuario?.login ?? '') },
    },
  ];

  protected readonly buscarPagina: CeiScrollableActionTablePageFetcher<PessoaListRow> = (request) =>
    this.api.listarPaginado(request);

  private readonly api = inject(PessoaUsuarioApiService);
  private readonly snackBar = inject(CeiSnackBarService);
  private readonly router = inject(Router);

  /** Navega para detalhes (com Pessoa) ou para cadastrar dados pessoais (só conta). */
  protected aoSelecionarLinha(linha: PessoaListRow): void {
    if (linha.pessoaId != null) {
      void this.router.navigate(['/admin/pessoas/detalhe', linha.pessoaId]);
      return;
    }
    const login = linha.login !== '—' ? linha.login : (linha.usuario?.login ?? '');
    void this.router.navigate(['/admin/pessoas/atualizar'], {
      queryParams: login ? { login } : { usuarioId: String(linha.usuario?.id ?? linha.id) },
    });
  }

  /** Exibe erro de carregamento remoto emitido pela tabela. */
  protected aoErroCarregamento(erro: unknown): void {
    notifyAdminListError(this.snackBar, 'Erro ao carregar pessoas e usuários.', erro);
  }

  /** Ajusta a largura da coluna ID conforme o maior valor carregado. */
  protected aoCarregarPagina(pagina: CeiTablePage<PessoaListRow>): void {
    const colunas = atualizarColunasIdAoCarregarPagina(this.larguraColunaId, this.colunasBase, pagina);
    if (colunas) {
      this.columns = colunas;
    }
  }
}
